import { Core } from "@faire/design-tokens";
import {
  ButtonBase,
  SymbolDiv,
} from "@faire/retailer-visitor-shared/components/Button/RoundButtonStyles";
import { FadeAnimatedContainer } from "@faire/retailer-visitor-shared/components/RoundQuantityPicker/__internal__/FadeAnimatedContainer";
import { RoundMultiOptionPicker } from "@faire/retailer-visitor-shared/components/RoundQuantityPicker/RoundMultiOptionPicker";
import {
  IRoundQuantityPickerStateProps,
  useRoundQuantityPickerState,
} from "@faire/retailer-visitor-shared/components/RoundQuantityPicker/UseRoundQuantityPickerState";
import { getRetailer } from "@faire/retailer-visitor-shared/serialized-data/getRetailer";
import { debounceIfPossible } from "@faire/web/common/globals/debounceIfPossible";
import { useStrictLocalization } from "@faire/web/common/localization";
import { DEPRECATED_backwardsCompatibleValues } from "@faire/web/common/localization/localize";
import { Add } from "@faire/web/slate/icons/foundation/Add";
import { Subtract } from "@faire/web/slate/icons/foundation/Subtract";
import { Column, Row } from "@faire/web/slate/Layout";
import {
  getSpacing,
  SpacerMultipliersKey,
} from "@faire/web/slate/spacing";
import { Span } from "@faire/web/slate/Typography";
import ClickOutside from "@faire/web/ui/ClickOutside";
import { useViewport } from "@faire/web/ui/hooks/useViewport";
import { trackRetailersRetailerOnChooseOptionFromQuickerAddClick } from "@faire/web-api--source/events/retailers/click/retailerOnChooseOptionFromQuickerAdd";
import React from "react";
import styled, { css } from "styled-components";

export type ErrorType = "max" | "min" | "other";
export interface IRoundQuantityPickerProps {
  disabled?: boolean;
  error?: ErrorType;
  size?: SpacerMultipliersKey;
  onIncrementEvent?: () => void;
  onDecrementEvent?: () => void;
  onChooseMultiOption?: () => void;
  isMultiOption?: boolean;
  maxWidth?: number;
  onMouseLeave?: () => void;
}
// 80 px provides the space for rendering
// - 2 side buttons: +(32px) / -(32px)
// - 2 paddings(each 8px)
//   (+ (padding) {digit} (padding) -)
const computeWidth = (stringLength: number) => stringLength * 8 + 80;
// If the value string is <= 2 digits, we want to show the round circle style, 32 px is to make sure we can render the round border radius correctly
// if number of digits > 2, we want to increment the width with 8px per digit
// ( single-digit ) <- 32 px width, 0 px reserved for digits
// ( xxx ) <- 40 px width, 32 px for round borders, 8 px for number
const computeBaseWidth = (stringLength: number) =>
  (stringLength > 2 ? stringLength - 2 : 0) * 8 + 32;
const FADE_ANIMATION_TIME_MS = 200;

/**
 * @trackcomponent
 */
export const RoundQuantityPicker: React.FC<
  IRoundQuantityPickerProps & IRoundQuantityPickerStateProps
> = ({
  onChange,
  value,
  minValue,
  maxValue,
  stepSize,
  disabled,
  size,
  error,
  onIncrementEvent,
  onDecrementEvent,
  isMultiOption,
  onChooseMultiOption,
  maxWidth,
  onMouseLeave,
}) => {
  const { strictLocalize } = useStrictLocalization();
  const [expanded, setExpanded] = React.useState<boolean>(error ? true : false);
  const [shouldDisableHoverExpand, setShouldDisableHoverExpand] =
    React.useState(false);

  const debouncedClose = React.useMemo(
    () => debounceIfPossible(() => setExpanded(false), 3000),
    []
  );
  React.useEffect(() => {
    if (expanded) {
      debouncedClose();
    }
  }, [value, expanded, debouncedClose]);

  const [pickerValue, dispatch] = useRoundQuantityPickerState({
    stepSize,
    minValue,
    maxValue,
    value,
    onChange,
  });

  const componentSize = getSpacing(size ?? "4x");
  const prevPickerValueLength = React.useRef<number>(-1);

  const [computedWidth, setComputedWidth] = React.useState<number>(
    computeWidth(String(pickerValue).length)
  );

  const [computedTextWidth, setComputedTextWidth] = React.useState<number>(
    computeBaseWidth(String(pickerValue).length)
  );

  const retailerToken = getRetailer()?.token;

  const { isDesktopAndAbove, isTabletAndBelow } = useViewport();

  React.useEffect(() => {
    const curPickerValueLength = String(pickerValue).length;

    if (curPickerValueLength !== prevPickerValueLength.current) {
      prevPickerValueLength.current = curPickerValueLength;
      setComputedWidth(computeWidth(String(pickerValue).length));
      setComputedTextWidth(computeBaseWidth(String(pickerValue).length));
    }
  }, [pickerValue]);

  if (isMultiOption) {
    return (
      <RoundMultiOptionPicker
        textWidth={computedTextWidth}
        data-test-id="quick-add-quantity-picker-multi-option"
        value={value}
        size={size}
        onChooseMultiOption={() => {
          trackRetailersRetailerOnChooseOptionFromQuickerAddClick(
            retailerToken ?? ""
          );
          onChooseMultiOption?.();
        }}
        maxWidth={maxWidth}
      />
    );
  }

  const expandComponent = () => {
    if (!shouldDisableHoverExpand) {
      if (!disabled) {
        setExpanded(true);
      }
    }
  };

  const renderContent = () => {
    if (value === 0 && !expanded) {
      return (
        <ButtonBase
          data-test-id="round-quantity-picker-expand-button"
          $size={componentSize}
          onClick={(e: { preventDefault: () => void }) => {
            e.preventDefault();
            expandComponent();
            dispatch("increment");
            onIncrementEvent?.();
          }}
          aria-label={strictLocalize(
            {
              defaultMessage:
                "{numberOfProductsAmount, plural, one {click to add # more product} other {click to add # more products}}",
              description: {
                text: "An aria label that notifies user on how many amount of products will be added from cart",
              },
            },
            DEPRECATED_backwardsCompatibleValues({
              numberOfProductsAmount: stepSize,
            })
          )}
          onMouseLeave={() => {
            setShouldDisableHoverExpand(false);
            onMouseLeave?.();
          }}
          disabled={disabled}
        >
          <SymbolDiv>
            <Add />
          </SymbolDiv>
        </ButtonBase>
      );
    } else if (!expanded) {
      return (
        <ButtonBase
          data-test-id="round-quantity-picker-expand-button-with-number"
          $size={componentSize}
          style={{ "--size": componentSize } as React.CSSProperties}
          aria-label={strictLocalize({
            defaultMessage: "quantity picker",
            description: {
              text: "An aria label tells user that it's a quantity picker",
            },
          })}
          onMouseEnter={() => {
            if (isDesktopAndAbove) {
              setExpanded(true);
            }
          }}
          onClick={(e) => {
            if (isTabletAndBelow) {
              e.preventDefault();
              e.stopPropagation();
              setExpanded(true);
            }
          }}
          disabled={disabled}
        >
          <Span variant="paragraphSansMedium" style={{ color: "inherit" }}>
            {pickerValue}
          </Span>
        </ButtonBase>
      );
    } else {
      return (
        <Row
          data-test-id="round-quantity-picker-main-content"
          justify="space-between"
          align="center"
          style={{ minWidth: `${computedWidth}px` }}
          onMouseLeave={() => {
            setExpanded(false);
            setShouldDisableHoverExpand(false);
            onMouseLeave?.();
          }}
        >
          <ButtonBase
            data-test-id="round-quantity-picker-decrement-button"
            $size={componentSize}
            style={{ "--size": componentSize } as React.CSSProperties}
            onClick={(e: { preventDefault: () => void }) => {
              e.preventDefault();
              dispatch("decrement");
              onDecrementEvent?.();
            }}
            aria-label={strictLocalize(
              {
                defaultMessage:
                  "{numberOfProductsAmount, plural, one {click to remove # product} other {click to remove # more products}}",
                description: {
                  text: "An aria label that notifies user on how many amount of products will be deducted from cart",
                },
              },
              DEPRECATED_backwardsCompatibleValues({
                numberOfProductsAmount: stepSize,
              })
            )}
          >
            <FadeAnimatedContainer
              willRender={expanded}
              fadeInDuration={FADE_ANIMATION_TIME_MS}
            >
              <SymbolDiv>
                <Subtract color={Core.text.inverse} />
              </SymbolDiv>
            </FadeAnimatedContainer>
          </ButtonBase>
          <Span variant="paragraphSansMedium" style={{ color: "inherit" }}>
            {pickerValue}
          </Span>
          <ButtonBase
            data-test-id="round-quantity-picker-increment-button"
            $size={componentSize}
            style={{ "--size": componentSize } as React.CSSProperties}
            aria-label={strictLocalize(
              {
                defaultMessage:
                  "{numberOfProductsAmount, plural, one {click to add # more product} other {click to add # more products}}",
                description: {
                  text: "An aria label that notifies user on how many amount of products will be added from cart",
                },
              },
              DEPRECATED_backwardsCompatibleValues({
                numberOfProductsAmount: stepSize,
              })
            )}
            onClick={(e: { preventDefault: () => void }) => {
              e.preventDefault();
              dispatch("increment");
              onIncrementEvent?.();
            }}
          >
            <FadeAnimatedContainer
              willRender={expanded}
              fadeInDuration={FADE_ANIMATION_TIME_MS}
            >
              <SymbolDiv>
                <Add color={Core.text.inverse} />
              </SymbolDiv>
            </FadeAnimatedContainer>
          </ButtonBase>
        </Row>
      );
    }
  };

  return (
    <ClickOutside
      onClickOutside={error ? undefined : () => setExpanded(false)}
      data-test-id="quick-add-quantity-picker-single-option"
    >
      <Column>
        <AnimatedBackground
          $maxWidth={computedWidth}
          $isBase={value === 0 && !expanded}
          $expand={expanded}
          $size={componentSize}
          $textWidth={computedTextWidth}
        >
          {renderContent()}
        </AnimatedBackground>
      </Column>
    </ClickOutside>
  );
};

const AnimatedBackground = styled.div<{
  $size: string;
  $expand: boolean;
  $isBase: boolean;
  $maxWidth: number;
  $textWidth: number;
}>`
  display: flex;
  align-items: center;
  justify-content: center;
  padding-left: ${(props) => (props.$expand ? getSpacing("2x") : "0")};
  padding-right: ${(props) => (props.$expand ? getSpacing("2x") : "0")};
  font-size: 16px;
  height: ${(props) => props.$size};
  background-color: ${(props) =>
    props.$isBase ? Core.surface.inverse : Core.surface.default};
  transition: all 200ms;
  transition-timing-function: ease-in-out;
  width: ${(props) =>
    // eslint-disable-next-line no-nested-ternary
    props.$expand
      ? css`
          ${props.$maxWidth}px
        `
      : props.$isBase
        ? props.$size
        : css`
            ${props.$textWidth}px
          `};

  border-radius: 16px;
  border: 1px solid;
  border-color: ${(props) =>
    props.$isBase ? Core.border.muted : Core.border.default};
  &:hover {
    border-color: ${Core.border.default};
  }
  color: ${(props) => (props.$isBase ? Core.text.default : Core.text.inverse)};
  ${({ $isBase }) =>
    $isBase
      ? css`
          &:hover {
            background-color: ${Core.surface.default};
            color: ${Core.border.muted};
          }
        `
      : null};
`;
